import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    location: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4dnlriahcjh6clrrm60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          location: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-amber-500 font-semibold text-sm uppercase tracking-wider">Contato</span>
          <div className="w-20 h-1 bg-amber-500 mx-auto mt-2 mb-6"></div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Vamos Criar Algo Inesquecível
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Entre em contato e descubra como podemos transformar seu evento em uma experiência musical única
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Contact Info */}
          <div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 border border-amber-500/20 mb-8">
              <h3 className="text-3xl font-bold text-white mb-8">Fale Conosco</h3>
              
              <div className="space-y-6">
                <a href="https://wa.me/5511970172813" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-green-600 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-whatsapp-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1 group-hover:text-amber-500 transition-colors">WhatsApp</h4>
                    <p className="text-gray-400">+55 11 97017-2813</p>
                    <p className="text-sm text-gray-500">Resposta rápida</p>
                  </div>
                </a>

                <a href="mailto:contato@feelmusic.com.br" className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1 group-hover:text-amber-500 transition-colors">E-mail</h4>
                    <p className="text-gray-400">contato@feelmusic.com.br</p>
                    <p className="text-sm text-gray-500">Envie sua proposta</p>
                  </div>
                </a>

                <a href="https://instagram.com/produtorafeelmusic" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-instagram-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1 group-hover:text-amber-500 transition-colors">Instagram</h4>
                    <p className="text-gray-400">@produtorafeelmusic</p>
                    <p className="text-sm text-gray-500">Veja nossos shows</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden h-80">
              <img
                src="https://public.readdy.ai/ai/img_res/b542d3a8f108b26213c1732257b314f1.jpg"
                alt="Feel Music Team"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-lg font-semibold">Estamos prontos para criar a trilha sonora do seu evento</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 border border-amber-500/20">
            <h3 className="text-3xl font-bold text-white mb-2">Solicite sua Proposta</h3>
            <p className="text-gray-400 mb-8">Preencha o formulário e entraremos em contato em breve</p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                  placeholder="Seu nome"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-300 mb-2">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="eventType" className="block text-sm font-semibold text-gray-300 mb-2">
                    Tipo de Evento *
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 pr-8 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                  >
                    <option value="">Selecione</option>
                    <option value="casamento">Casamento</option>
                    <option value="corporativo">Evento Corporativo</option>
                    <option value="shopping">Shopping</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="eventDate" className="block text-sm font-semibold text-gray-300 mb-2">
                    Data do Evento
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-semibold text-gray-300 mb-2">
                  Local do Evento
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                  placeholder="Cidade ou endereço"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-2">
                  Mensagem
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 transition-colors resize-none"
                  placeholder="Conte-nos mais sobre seu evento..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caracteres</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-2xl text-green-500"></i>
                  <p className="text-green-500 font-semibold">Mensagem enviada com sucesso! Entraremos em contato em breve.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-error-warning-line text-2xl text-red-500"></i>
                  <p className="text-red-500 font-semibold">Erro ao enviar mensagem. Tente novamente.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-bold text-lg rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-3">
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    Solicitar Proposta Completa
                    <i className="ri-send-plane-fill text-xl"></i>
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
